"use client";

import type { ReactNode } from "react";
import { useUser } from "../hooks/useUser";
import { useAuthModal } from "../hooks/useAuthModal";
import { usePricingModal } from "../hooks/usePricingModal";
import type { UserPlan } from "@/lib/auth/plans";

type RequireAuthProps = {
  /** Minimum plan required. Defaults to "free" (any authenticated user). */
  minPlan?: "free" | "pro";
  /** Shown to users who don't meet the requirement. Falls back to a default prompt. */
  fallback?: ReactNode;
  children: ReactNode;
};

const PLAN_RANK: Record<UserPlan, number> = {
  anonymous: 0,
  free: 1,
  pro: 2,
  studio: 3,
};

export function RequireAuth({
  minPlan = "free",
  fallback,
  children,
}: RequireAuthProps) {
  const { plan, loading } = useUser();
  const { open } = useAuthModal();
  const { open: openPricing } = usePricingModal();

  if (loading) return null;

  const hasAccess = PLAN_RANK[plan] >= PLAN_RANK[minPlan];

  if (hasAccess) return <>{children}</>;

  if (fallback) return <>{fallback}</>;

  if (plan === "anonymous") {
    return (
      <div className="flex flex-col items-center gap-3 py-8 text-center">
        <p className="text-sm text-[#8f98a0]">
          Sign in to access this feature
        </p>
        <button
          type="button"
          onClick={open}
          className="rounded-xl bg-[#4c9cd5] px-5 py-2 text-sm font-semibold text-[#0a0f15] hover:bg-[#5aa9de] transition-colors"
        >
          Sign in
        </button>
      </div>
    );
  }

  return (
    <div className="flex flex-col items-center gap-3 py-8 text-center">
      <p className="text-sm text-[#8f98a0]">
        This feature is part of Pro
      </p>
      <button
        type="button"
        onClick={() => openPricing("require_auth")}
        className="rounded-xl bg-[#66c0f4] px-5 py-2 text-sm font-semibold text-[#0a0f15] hover:brightness-110 transition"
      >
        See plans
      </button>
    </div>
  );
}
